// Phase 9.2 — Public Certificate Verification Page
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Award, Copy, CheckCircle2, XCircle, ExternalLink, ShieldAlert } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicCertificate() {
  const { cert_id } = useParams();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/certificates/${cert_id}`);
        setCert(data);
      } catch (_) { setErr("Certificate Not Found or Not Verified"); }  // eslint-disable-line no-unused-vars
    })();
  }, [cert_id]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (_) { /* noop */ }  // eslint-disable-line no-unused-vars
  };

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-lifted-bg px-4" data-testid="cert-not-found">
        <div className="glass-panel p-8 text-center max-w-md">
          <ShieldAlert className="h-10 w-10 mx-auto text-rose-300 mb-2" />
          <h1 className="font-anton text-2xl uppercase">Certificate Not Verified</h1>
          <p className="text-sm text-lifted-dim mt-2">{err}</p>
          <Link to="/" className="text-xs text-lifted-green mt-4 inline-flex items-center gap-1">
            Visit LiftedAtTheMoment.com <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    );
  }
  if (!cert) return <div className="min-h-screen grid place-items-center text-lifted-dim text-sm">Verifying…</div>;

  if (cert.status === "revoked") {
    return (
      <div className="min-h-screen grid place-items-center bg-lifted-bg px-4" data-testid="cert-revoked">
        <div className="glass-panel p-8 text-center max-w-md">
          <XCircle className="h-10 w-10 mx-auto text-rose-300 mb-2" />
          <h1 className="font-anton text-2xl uppercase">Certificate Revoked</h1>
          <p className="text-sm text-lifted-dim mt-2">{cert.message}</p>
          <div className="text-[0.65rem] font-mono text-lifted-dim mt-2">{cert.certification_id}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lifted-bg px-4 py-10" data-testid="cert-verified">
      <div className="max-w-3xl mx-auto">
        <div className="glass-panel p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
            background: "radial-gradient(circle at 20% 20%, rgba(184,255,103,.5), transparent 40%), radial-gradient(circle at 80% 80%, rgba(184,255,103,.3), transparent 50%)",
          }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[0.65rem] font-bebas tracking-[0.22em] text-lifted-green uppercase">
              <CheckCircle2 className="h-4 w-4" /> Verified
            </div>
            <div className="font-anton text-5xl uppercase mt-3 leading-tight">
              {cert.title}
            </div>

            <div className="text-lifted-dim text-sm sm:text-base mt-6 leading-relaxed" data-testid="cert-citation">
              This certifies that <span className="text-lifted-text font-medium">{cert.name}</span>
              has successfully completed the <span className="text-lifted-green capitalize">{cert.role}</span> Certification
              for the LiftedAtTheMoment Ecosystem.
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <Field label="Certificate ID" value={cert.certification_id} mono />
              <Field label="Issued" value={cert.issued_at?.slice(0, 10)} />
              <Field label="Score" value={cert.score != null ? `${cert.score} / ${cert.total}` : "—"} />
            </div>

            <div className="mt-8 text-[0.6rem] font-bebas tracking-[0.22em] text-lifted-dim uppercase">
              Issued by · {cert.issuer}
            </div>

            <div className="mt-6 flex flex-wrap gap-2 items-center">
              <button onClick={copyLink}
                      className="btn-lifted !py-2 !px-3 text-xs inline-flex items-center gap-1.5"
                      data-testid="cert-copy">
                <Copy className="h-3.5 w-3.5" /> {copied ? "Copied" : "Copy share link"}
              </button>
              <a href="https://liftedatthemoment.com" target="_blank" rel="noreferrer"
                 className="btn-ghost text-xs inline-flex items-center gap-1.5">
                <Award className="h-3.5 w-3.5" /> Visit LiftedAtTheMoment
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="glass-panel p-3">
      <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim">{label}</div>
      <div className={`text-lifted-text mt-1 ${mono ? "font-mono text-[0.75rem]" : ""}`}>{value}</div>
    </div>
  );
}
